import React, { useRef, useCallback, useContext } from "react";
import { Grid } from "@mui/material";

import { GlobalContext } from "@/helpers/contexts/contexts";
import MuiSearchInput from "@/components/muiCustom/search/MuiSearchInput";
import FilterDropdownBase from "@/components/filters/FilterDropdown";
import TagChipContainer from "@/components/muiCustom/chip/TagChip";
import ParticipantFilter from "./ParticipantFilter";
import useFetcher from "@/helpers/hooks/useFetcher";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";

const FilterDropdown = FilterDropdownBase as React.ComponentType<any>;

const TAG_CHIP_STYLES = {
  height: "34px",
  fontSize: ".6rem",
  cursor: "pointer",
  margin: "3px",
  borderRadius: "var(--radius-control)",
  fontWeight: 700,
  color: "var(--palette-text-primary)",
  backgroundColor: "transparent",
  border: true,
  minWidth: 80,
};

const normalizeFilterTag = (filter) => ({
  id: filter.id,
  color_code: filter.colorCode ?? filter.color_code,
  ...filter,
});

const TABS = { DIGITAL: 0, DIRECT_MAIL: 1 };

type ParticipantFilterValue = {
  id: string | number;
  systemId?: string | number;
  name?: string;
  avatar?: string | null;
};

type FilterRecord = {
  id: string | number;
  colorCode?: string;
  color_code?: string;
  [key: string]: unknown;
};

type OverviewFilters = {
  search?: string;
  tags?: FilterRecord[];
  channels?: FilterRecord[];
  groups?: Array<string | number>;
  participant?: ParticipantFilterValue | null;
};

type CampaignOverviewFiltersProps = {
  filters: OverviewFilters;
  participants?: ParticipantFilterValue[];
  isParticipantsLoading?: boolean;
  onFilterChange: (_key: string, _value: unknown) => void;
  activeTab: number;
};

function CampaignOverviewFilters({
  filters,
  participants = [],
  onFilterChange,
  activeTab,
}: CampaignOverviewFiltersProps) {
  const { selectedBrand } = useContext(GlobalContext);
  const filterDropdownRef = useRef<{
    handleRemoveSelectedFilter?: (_filter: FilterRecord) => void;
  } | null>(null);
  const brandId = selectedBrand?.id;
  const participantId = filters.participant?.id;

  const tagsReqData =
    brandId && participantId
      ? { id: brandId, fk_participant_id: participantId }
      : null;

  const { data: digitalTagsData } = useFetcher(
    activeTab === TABS.DIGITAL ? tagsReqData : null,
    campaignService.getTagsAndChannelsForParticipant,
    { revalidateOnFocus: false }
  );

  const { data: directMailTagsData } = useFetcher(
    activeTab === TABS.DIRECT_MAIL ? tagsReqData : null,
    campaignService.getPostCardCampaignTagsParticipant,
    { revalidateOnFocus: false }
  );

  const tagsList =
    activeTab === TABS.DIRECT_MAIL
      ? directMailTagsData?.tagsList || []
      : digitalTagsData?.tagsList || [];
  const channelsList =
    activeTab === TABS.DIGITAL ? digitalTagsData?.channels || [] : [];

  const handleSearchChange = useCallback(
    (value) => onFilterChange("search", value),
    [onFilterChange]
  );

  const handleRemoveFilter = useCallback((filter: FilterRecord) => {
    filterDropdownRef.current?.handleRemoveSelectedFilter(filter);
  }, []);

  const activeFilters = [
    ...new Map(
      [...(filters?.channels || []), ...(filters?.tags || [])].map((f) => [
        f.id,
        f,
      ])
    ).values(),
  ];

  return (
    <div className="my-4">
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <MuiSearchInput
            value={filters.search}
            onChange={handleSearchChange}
            placeholder="Search.."
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: "auto" }}>
          <FilterDropdown
            channelsList={channelsList}
            tagsList={tagsList}
            onApplyFilters={(type, value) => onFilterChange(type, value)}
            filters={filters}
            ref={filterDropdownRef}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <ParticipantFilter
            participants={participants}
            value={filters.participant?.id ?? null}
            onChange={(value) => {
              const selected = participants.find((p) => p.id === value) ?? null;
              onFilterChange("participant", selected);
            }}
          />
        </Grid>
      </Grid>

      {activeFilters.length > 0 && (
        <div className="p-0 mt-2">
          <TagChipContainer
            tags={activeFilters.map(normalizeFilterTag)}
            expandTags={false}
            customTagStyles={TAG_CHIP_STYLES}
            showRemoveIcon
            onRemoveTag={handleRemoveFilter}
          />
        </div>
      )}
    </div>
  );
}

export default CampaignOverviewFilters;
